import { graphql } from 'react-relay';
import React, { useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import Heading from '@atlaskit/heading/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Text from '@atlaskit/primitives/text';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Button from '@atlaskit/button/default/button';
import SectionMessage from '@atlaskit/section-message/message';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useClassificationChangeState } from '@confluence/data-classification/entry-points/useClassificationChangeState.tsx';
import { usePageState } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { SelectedSpaceOnChangeProps } from '@confluence/space-selector';
import { SpaceSelector } from '@confluence/space-selector';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useVersionHistoryExperienceTracking } from '@confluence/content-types-utils/entry-points/useVersionHistoryExperienceTracking.ts';
import { fg } from '@confluence/feature-gating';

import type { useVersionHistoryContentVersionHistoryQuery_contentVersionHistory_ContentVersionHistoryConnection_nodes_ContentVersion as VersionHistoryNodeType } from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import type {
	RestoreContentModalRestoreContentMutation as RestoreContentModalRestoreContentMutation$data,
	RestoreContentModalRestoreContentMutationVariables as RestoreContentModalRestoreContentMutation$variables,
} from './__types__/RestoreContentModalRestoreContentMutation.ts';
import { getFormattedDate } from './versionHistoryUtils.ts';
import { useVersionHistoryAnalytics } from './useVersionHistoryAnalytics.ts';
import { VersionTitleUpdate } from './VersionTitleUpdate.tsx';
const i18n = defineMessages({
	restoreContentTitle: {
		id: 'version-history.restore.content.title',
		defaultMessage: 'Restore',
		description:
			'The text is shown as the heading of a modal dialog that appears when the user chooses to restore a previous version of a content item (e.g. whiteboard or database) from the version history panel.',
	},
	restoreButton: {
		id: 'version-history.restore.button',
		defaultMessage: 'Restore',
		description:
			'The text is shown on the primary action button in the restore content modal dialog footer. When clicked, it confirms and initiates the restoration of the selected content version.',
	},
	whiteboardInfoBoxText: {
		id: 'version-history.info.box.text',
		defaultMessage: 'When restoring a board, permission and share settings remain the same.',
		description:
			'Info message that indicates that the new board will inherit restrictions and share settings of the original',
	},
	saveWorkWithoutCreatingText: {
		id: 'version-history.save-work-without-creating.text',
		defaultMessage:
			'To save work without creating, try copying content from the whiteboard to an editable whiteboard.',
		description: 'Message informing users another way to saving work without creating',
	},
	spaceSelectorLabel: {
		id: 'version-history.space-selector.label',
		defaultMessage: 'Select space',
		description: 'Label for the space selector in the restore modal',
	},
	whiteboardPlaceholderTitle: {
		id: 'version-history.placeholder.whiteboard.title',
		defaultMessage: '[Restored] Untitled board {date}',
		description: 'Title name for the version selected to be restored as a new whiteboard',
	},
	duplicateTitleErrorTitle: {
		id: 'version-history.restore-modal.conflicting-title-error-title',
		defaultMessage: 'Duplicate title in this space',
		description:
			'Title for error section that appears when users try to restore with an existing title',
	},
	newDuplicateTitleErrorBody: {
		id: 'version-history.restore-modal.new-conflicting-title-error-body',
		defaultMessage:
			'To continue restoring, create a unique title or try adding it to a different space.',
		description:
			'Generalized text that appears when users try to restore with a duplicate title telling them potential actions to resolve the error',
	},
	spacePermissionErrorTitle: {
		id: 'version-history.restore-modal.space-permission-error-title',
		defaultMessage: 'You don’t have space access',
		description:
			'Title for error section that appears when users try to restore content into a space where they do not have permission to create in',
	},
	newSpacePermissionErrorBody: {
		id: 'version-history.restore-modal.new-space-permission-error-body',
		defaultMessage:
			'To restore this content, you’ll need permissions to <b>Create</b> in this space.',
		description:
			'Generalized text that appears when users attempt to restore content in a space where they do not have create permissions',
	},
	genericErrorTitle: {
		id: 'version-history.restore-modal.generic-error-title',
		defaultMessage: 'We couldn’t complete your request',
		description:
			'Title for error section that appears when users try to restore content and the request is not successful',
	},
	genericErrorBody: {
		id: 'version-history.restore-modal.generic-error-body',
		defaultMessage: 'Try refreshing the page, or check your network connection.',
		description: 'Generic error message that appears when restoring content fails',
	},
	databasePlaceholderTitle: {
		id: 'version-history.placeholder.database.title',
		defaultMessage: '[Restored] Untitled database {date}',
		description: 'Title name for the version selected to be restored as a new database',
	},
	databaseInfoBoxText: {
		id: 'version-history.database.info.box.text',
		defaultMessage: 'When restoring a database, permission and share settings remain the same.',
		description:
			'Info message that indicates that the new database will inherit restrictions and share settings of the original',
	},
	slidePlaceholderTitle: {
		id: 'version-history.placeholder.slide.title',
		defaultMessage: '[Restored] Untitled slide deck {date}',
		description: 'Title name for the version selected to be restored as a new slide deck',
	},
	slideInfoBoxText: {
		id: 'version-history.slide.info.box.text',
		defaultMessage: 'When restoring a slide deck, permission and share settings remain the same.',
		description:
			'Info message that indicates that the new slide deck will inherit restrictions and share settings of the original',
	},
});

type RestoreContentModalTypeMapping = {
	[key: string]: {
		description: MessageDescriptor;
		placeholderTitle: MessageDescriptor;
	};
};

const restoreContentModalMessaging: RestoreContentModalTypeMapping = {
	whiteboard: {
		description: i18n.whiteboardInfoBoxText,
		placeholderTitle: i18n.whiteboardPlaceholderTitle,
	},
	database: {
		description: i18n.databaseInfoBoxText,
		placeholderTitle: i18n.databasePlaceholderTitle,
	},
	slide: {
		description: i18n.slideInfoBoxText,
		placeholderTitle: i18n.slidePlaceholderTitle,
	},
};

type RestoreContentModalTypes = {
	onClose: () => void;
	selectedVersion: VersionHistoryNodeType;
	contentType: string;
};

type ErrorSectionMessageProps = {
	error: Error | null;
};

const EXPECTED_ERROR_MESSAGE = {
	duplicateTitle: 'exists with the same title in this space',
	spacePermissions: '.PermissionException: Could not create content',
};

const errorSectionMessageStyles = xcss({
	marginBottom: 'space.100',
});

const ErrorSectionMessage = ({ error }: ErrorSectionMessageProps) => {
	const { formatMessage } = useIntl();
	let errorTitle;
	let errorBody;
	if (!error) {
		return null;
	}

	if (error.message.includes(EXPECTED_ERROR_MESSAGE.duplicateTitle)) {
		errorTitle = i18n.duplicateTitleErrorTitle;
		errorBody = i18n.newDuplicateTitleErrorBody;
	} else if (error.message.includes(EXPECTED_ERROR_MESSAGE.spacePermissions)) {
		errorTitle = i18n.spacePermissionErrorTitle;
		errorBody = i18n.newSpacePermissionErrorBody;
	} else {
		errorTitle = i18n.genericErrorTitle;
		errorBody = i18n.genericErrorBody;
	}

	return (
		<Box xcss={errorSectionMessageStyles}>
			<ErrorDisplay error={error}>
				<SectionMessage appearance="error" title={formatMessage(errorTitle)}>
					<FormattedMessage
						{...errorBody}
						values={{
							b: (chunks) => <b>{chunks}</b>,
						}}
					/>
				</SectionMessage>
			</ErrorDisplay>
		</Box>
	);
};

export const RestoreContentModal = ({
	onClose,
	selectedVersion,
	contentType,
}: RestoreContentModalTypes): React.JSX.Element => {
	const { formatMessage, formatDate, formatTime } = useIntl();
	const { onRestore } = useVersionHistoryAnalytics();
	const selectedVersionDate = getFormattedDate(selectedVersion.updatedTime, formatDate, formatTime);

	// use current space as default space key
	const [{ spaceKey, contentId }] = usePageState();
	const [destinationSpaceKey, setDestinationSpaceKey] = useState<string | undefined>(spaceKey);
	const [title, setTitle] = useState<string>(
		formatMessage(restoreContentModalMessaging[contentType].placeholderTitle, {
			date: selectedVersionDate,
		}),
	);
	const [restoreError, setRestoreError] = useState<Error | null>(null);
	const [restoreIsActive, setRestoreIsActive] = useState(false);
	const { push } = useRouteActions();
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'restore-content-modal',
	});

	const { selectedSpaceClassification, classificationError } = useClassificationChangeState(
		String(contentId),
		destinationSpaceKey,
		spaceKey,
	);
	const { hideSidePanel } = useSidePanelStateActions();

	const requiredSpaceOperations = [
		{
			targetType: 'page',
			operation: 'create',
		},
	];

	const onSpaceSelectorChange = (selectedSpace: SelectedSpaceOnChangeProps) => {
		const { spaceKey: selectedSpaceKey } = selectedSpace;
		setDestinationSpaceKey(selectedSpaceKey);
	};

	const { trackExperience } = useVersionHistoryExperienceTracking();
	const [restoreVersion] = useMutationInterop<
		RestoreContentModalRestoreContentMutation$data,
		RestoreContentModalRestoreContentMutation$variables
	>(
		graphql`
			mutation RestoreContentModalRestoreContentMutation(
				$spaceKey: String!
				$restoreVersionContentId: String!
				$title: String
				$type: String!
			) {
				createContentContextual(
					input: {
						spaceKey: $spaceKey
						contentSpecificCreateInput: [
							{ key: "restoreVersionContentId", value: $restoreVersionContentId }
						]
						status: CURRENT
						title: $title
						type: $type
					}
				) {
					id
					links {
						context
						webui
					}
				}
			}
		`,
		{
			onCompleted: (data) => {
				setRestoreIsActive(false);
				const contentUrl = `${data?.createContentContextual?.links?.context}${data?.createContentContextual?.links?.webui}?flow=restoreVersion`;
				trackExperience({
					contentType,
					experience: 'restoreExperience',
					task: 'start',
				});
				onRestore(selectedVersion);
				hideSidePanel();
				push(contentUrl);
			},
			onError: (graphqlError) => {
				setRestoreIsActive(false);
				setRestoreError(graphqlError);
			},
		},
	);

	const handleRestoreClick = useCallback(() => {
		setRestoreIsActive(true);
		setRestoreError(null);
		void restoreVersion({
			variables: {
				spaceKey: destinationSpaceKey!,
				title,
				type: contentType,
				restoreVersionContentId: selectedVersion.contentId,
			},
		});
	}, [restoreVersion, destinationSpaceKey, title, contentType, selectedVersion]);

	useEffect(() => {
		// succeed restore modal load experience on render
		trackExperience({
			contentType,
			experience: 'restoreModal',
			task: 'succeed',
		});
	}, [contentType, trackExperience]);

	return (
		<Modal testId="restore-content-modal" width="400px" onClose={onClose}>
			<ModalHeader hasCloseButton>
				{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
				{fg('platform_dst_modal-dialog-use-modal-title') ? (
					<ModalTitle>
						<FormattedMessage {...i18n.restoreContentTitle} />
					</ModalTitle>
				) : (
					<Heading size="medium" as="h6">
						<FormattedMessage {...i18n.restoreContentTitle} />
					</Heading>
				)}
			</ModalHeader>
			<ModalBody>
				<ErrorSectionMessage error={restoreError} />
				<Text size="medium">
					<FormattedMessage {...restoreContentModalMessaging[contentType].description} />
				</Text>
				<VersionTitleUpdate title={title} setTitle={setTitle} contentType={contentType} />
				<SpaceSelector
					onChange={onSpaceSelectorChange}
					operationsUsedToDetermineRestrictedStatus={requiredSpaceOperations}
					showRestrictedSpaces={false}
					filterDefaultFromResults={false}
					spaceKey={destinationSpaceKey}
					labelTextTransform="none"
					useMenuPortalTarget
					spaceClassification={selectedSpaceClassification}
					hasClassificationError={!!classificationError}
					selectStyleOverrides={{
						menuPortal: (base) => ({
							...base,
							zIndex: 9999,
						}),
					}}
				/>
			</ModalBody>
			<ModalFooter>
				<Button
					appearance="primary"
					onClick={enforceStorageLimit(handleRestoreClick)}
					isLoading={restoreIsActive}
					isDisabled={restoreIsActive}
					testId="restore-content-button"
				>
					<FormattedMessage {...i18n.restoreButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
